import { useEffect, useMemo, useRef, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { TimelineEvent, EventKind } from '../types/Timeline';
import { authFetch } from '../lib/authFetch';

type Filters = {
  dateFrom?: string;
  dateTo?: string;
  kinds?: EventKind[] | string[];
  goalId?: string;
  milestoneId?: string;
  search?: string;
};

const parseApiError = async (res: Response, fallback: string) => {
  try {
    const json = await res.json();
    if (json && typeof json.message === 'string' && json.message.trim()) return json.message;
  } catch {
    // no-op
  }
  return fallback;
};

const qs = (obj: Record<string, any>) => {
  const p = new URLSearchParams();
  Object.entries(obj).forEach(([k, v]) => {
    if (v == null || v === '') return;
    if (Array.isArray(v)) p.append(k, v.join(','));
    else p.append(k, String(v));
  });
  const s = p.toString();
  return s ? `?${s}` : '';
};

export const useTimeline = (refreshKey = 0, filters: Filters = {}) => {
  const { auth } = useAuth();
  const [events, setEvents] = useState<TimelineEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [reloadKey, setReloadKey] = useState(0);
  const reqRef = useRef(0);
  const filtersKey = useMemo(() => JSON.stringify(filters), [filters]);

  useEffect(() => {
    let cancelled = false; const id = ++reqRef.current;
    const fetchItems = async () => {
      setLoading(true);
      setError(null);
      try {
        const res = await authFetch(`/api/timeline${qs(filters)}`, {
          headers: { Authorization: `Bearer ${auth.token}` },
        });
        if (!res.ok) {
          const message = await parseApiError(res, 'Failed to load timeline');
          if (!cancelled && id === reqRef.current) {
            setEvents([]);
            setError(message);
          }
          return;
        }
        const json = await res.json(); // { data: TimelineEvent[] }
        if (!cancelled && id === reqRef.current) setEvents(json.data || []);
      } catch (e) {
        if (!cancelled && id === reqRef.current) {
          setEvents([]);
          setError('Failed to load timeline');
        }
      } finally {
        if (!cancelled && id === reqRef.current) setLoading(false);
      }
    };
    fetchItems();
    return () => { cancelled = true; };
  }, [auth.token, refreshKey, filtersKey, reloadKey]);

  const reload = () => setReloadKey((k) => k + 1);

  const createEvent = async (payload: Partial<TimelineEvent>) => {
    setError(null);
    const res = await authFetch('/api/timeline', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${auth.token}` },
      body: JSON.stringify(payload),
    });
    if (!res.ok) {
      const message = await parseApiError(res, 'Failed to create timeline event');
      setError(message);
      throw new Error(message);
    }
    const json = await res.json();
    setEvents(prev => [json.data, ...prev]);
    return json.data as TimelineEvent;
  };

  const deleteEvent = async (id: string) => {
    setError(null);
    // optimistic
    const prevEvents = events;
    setEvents(prev => prev.filter(e => e._id !== id));
    const res = await authFetch(`/api/timeline/${id}`, {
      method: 'DELETE',
      headers: { Authorization: `Bearer ${auth.token}` },
    });
    if (!res.ok) {
      const message = await parseApiError(res, 'Failed to delete timeline event');
      setEvents(prevEvents);
      setError(message);
      throw new Error(message);
    }
  };

  return { events, loading, error, reload, createEvent, deleteEvent, setEvents };
};
